import hotkeys from "hotkeys-js";

declare global {
  interface Window {
    __WM_GM_HOTKEYS_BOOTED?: boolean;
    toggleHighContrast?: () => void;
  }
}

const HOTKEY_SCOPE = "wm-global";
const GO_TIMEOUT_MS = 1200;

type AppRole = "gm" | "volunteer" | "participant";

let goTimer: number | null = null;
let goPending = false;

function resolveRole(): AppRole {
  const bodyRole = String(document.body?.dataset.appRole || "").trim().toLowerCase();
  if (bodyRole === "gm" || bodyRole === "volunteer" || bodyRole === "participant") {
    return bodyRole;
  }
  if (window.location.pathname.startsWith("/gm")) return "gm";
  if (window.location.pathname.startsWith("/volunteer")) return "volunteer";
  return "participant";
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const editableParent = target.closest("[contenteditable='true']");
  if (editableParent instanceof HTMLElement) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA";
}

function shouldIgnore(event?: KeyboardEvent): boolean {
  if (event?.defaultPrevented) return true;
  const target = event?.target ?? document.activeElement;
  return isEditableTarget(target);
}

function emit(name: string, detail?: Record<string, string>, cancelable = false): boolean {
  return document.body?.dispatchEvent(new CustomEvent(name, { bubbles: true, detail, cancelable })) ?? true;
}

function clearGoPending(): void {
  goPending = false;
  if (goTimer !== null) {
    window.clearTimeout(goTimer);
    goTimer = null;
  }
}

function armGoPending(): void {
  clearGoPending();
  goPending = true;
  goTimer = window.setTimeout(() => {
    clearGoPending();
  }, GO_TIMEOUT_MS);
}

function navigateForSequence(key: string): boolean {
  const role = resolveRole();
  if (role === "gm") {
    if (key === "q") {
      if (!emit("wm-hotkey-navigation", { path: "/gm/qrcodes" }, true)) return true;
      window.location.href = "/gm/qrcodes";
      return true;
    }
    if (key === "o") {
      if (!emit("wm-hotkey-navigation", { path: "/gm/" }, true)) return true;
      window.location.href = "/gm/";
      return true;
    }
    if (key === "s") {
      if (!emit("wm-hotkey-navigation", { path: "/gm/submissions" }, true)) return true;
      window.location.href = "/gm/submissions";
      return true;
    }
  }
  if (role === "volunteer" && key === "v") {
    if (!emit("wm-hotkey-navigation", { path: "/volunteer/" }, true)) return true;
    window.location.href = "/volunteer/";
    return true;
  }
  return false;
}

function bindGlobalHotkeys(): void {
  hotkeys.unbind("ctrl+k,command+k", HOTKEY_SCOPE);
  hotkeys.unbind("shift+/", HOTKEY_SCOPE);
  hotkeys.unbind("alt+shift+h", HOTKEY_SCOPE);
  hotkeys.unbind("g", HOTKEY_SCOPE);
  hotkeys.unbind("q", HOTKEY_SCOPE);
  hotkeys.unbind("o", HOTKEY_SCOPE);
  hotkeys.unbind("s", HOTKEY_SCOPE);
  hotkeys.unbind("v", HOTKEY_SCOPE);

  hotkeys("ctrl+k,command+k", HOTKEY_SCOPE, (event) => {
    if (shouldIgnore(event)) return true;
    event?.preventDefault();
    emit("wm-hotkey-command-palette");
    return false;
  });

  hotkeys("shift+/", HOTKEY_SCOPE, (event) => {
    if (shouldIgnore(event)) return true;
    event?.preventDefault();
    emit("wm-hotkey-shortcuts");
    return false;
  });

  hotkeys("alt+shift+h", HOTKEY_SCOPE, (event) => {
    if (shouldIgnore(event)) return true;
    event?.preventDefault();
    if (typeof window.toggleHighContrast === "function") {
      window.toggleHighContrast();
    } else {
      emit("wm-hotkey-high-contrast");
    }
    return false;
  });

  hotkeys("g", HOTKEY_SCOPE, (event) => {
    if (shouldIgnore(event)) return true;
    event?.preventDefault();
    armGoPending();
    return false;
  });

  ["q", "o", "s", "v"].forEach((key) => {
    hotkeys(key, HOTKEY_SCOPE, (event) => {
      if (shouldIgnore(event)) return true;
      if (!goPending) return true;
      clearGoPending();
      if (!navigateForSequence(key)) return true;
      event?.preventDefault();
      return false;
    });
  });

  hotkeys.setScope(HOTKEY_SCOPE);
}

function initGMHotkeys(): void {
  if (window.__WM_GM_HOTKEYS_BOOTED) return;
  const role = resolveRole();
  if (role !== "gm" && role !== "volunteer") return;
  bindGlobalHotkeys();
  window.__WM_GM_HOTKEYS_BOOTED = true;
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initGMHotkeys, { once: true });
} else {
  initGMHotkeys();
}
